/* eslint-disable react/no-unused-state */
import React from 'react';
import { HTTP_METHOD } from '@corporate-initiatives/ci-portal-js-sdk';
import moment from 'moment';

import {
  IRenewContractModalWizardState,
  IRenewContractModalWizardPage1Data,
  renewContractModalWizardInitialState,
  IRenewContractModalWizardPage2Data,
  IRenewContractModalWizardPage3Data,
} from './renew-contract-data.interface';
import { IModalWizardPageDataError } from '../../../types/modal/modal-wizard-page-data-error.interface';
import { IModalWizardPageData } from '../../../types/modal/modal-wizard-page-data.interface';
import { IContractRecord } from '../../../types/service/contract.record.interface';
import { ConfirmActionModalResult } from '../../../types/modal/modal-result';
import { ModalProps } from '../../../types/modal/modal.props';

import { APIContextProps, connectToAPIProvider } from '../../providers/api-provider';

import { ModalWizard } from '../modal-wizard';
import { RenewContractModalPage1 } from './wizard-pages/renew-contract-modal-page-1';
import { RenewContractModalPage2 } from './wizard-pages/renew-contract-modal-page-2';

import { SERObject } from '../../../helpers/ser-object.helper';
import { apiAborter } from '../../../helpers/api-aborter.helper';

import { THEME_COLOR } from '../../../constants/theme-color.const';
import { MODAL_WIZARD_BUTTON_TYPE, A_MODAL_WIZARD_BUTTON_TYPE } from '../../../constants/modal-wizard-button-type.const';
import { A_RENEW_CONTRACT_MODAL_WIZARD_PAGE, RENEW_CONTRACT_MODAL_WIZARD_PAGE } from './renew-contract-modal-wizard-page.const';
import { IContractCompanySpaceRecord } from '../../../types/contact/contract-company-space.record.interface';
import { RenewContractModalPage3 } from './wizard-pages/renew-contract-modal-page-3';

export type RenewContractModalWizardProps = ModalProps<ConfirmActionModalResult> & {
  apiProvider: APIContextProps,
  initialData: IContractRecord,
  onModalComplete: (success: boolean) => unknown,
}

class RenewContractModalWizardComponent extends React.Component<RenewContractModalWizardProps, IRenewContractModalWizardState> {
  private renewContractAbortController: null | AbortController;

  private loadContractCompanySpacesAbortController: null | AbortController;

  /**
   * @constructor
   */
  constructor(props: RenewContractModalWizardProps) {
    super(props);
    this.state = renewContractModalWizardInitialState;

    this.renewContractAbortController = null;
    this.loadContractCompanySpacesAbortController = null;
  }


  /**
   * @inheritdoc
   */
  componentDidMount = () => {
    this.initialise();
  }


  /**
   * @inheritdoc
   */
  componentWillUnmount = () => {
    if (this.renewContractAbortController) this.renewContractAbortController.abort();
    if (this.loadContractCompanySpacesAbortController) this.loadContractCompanySpacesAbortController.abort();
  }


  /**
   * When the wizard is loaded, we have to fetch some pre-existing information about the contract
   */
  private initialise = async () => {
    // The initial data is a contract record. Just use that
    const { initialData: contractRecord } = this.props;
    const { pageData } = this.state;

    this.setState({
      contractRecord,
    }, async () => {
      // Look for the invoicing project_contact
      const loadContractCompanySpacesResult = await this.loadContractCompanySpaces();
      const contractSpaces: IContractCompanySpaceRecord[] | null = loadContractCompanySpacesResult.result;
      const cover_spaces: { [key: string]: 1|0 } = {};
      contractSpaces?.map((space) => {
        cover_spaces[space.id] = 1;
        return space.id;
      });

      this.setState({
        contractSpaces,

        // Populate some of the page data from the contract record
        pageData: {
          ...pageData,

          // Page 1
          [RENEW_CONTRACT_MODAL_WIZARD_PAGE.PAGE_1_NEW_CONTRACT_SETTINGS]: {
            ...pageData[RENEW_CONTRACT_MODAL_WIZARD_PAGE.PAGE_1_NEW_CONTRACT_SETTINGS],
            automatically_generate_renewal: 1,
            contract_months: contractRecord.contract_months,
            start_date: contractRecord.end_date,
            end_date: contractRecord.contract_months ?
              moment(contractRecord.end_date).add(contractRecord.contract_months, 'months').format('Y-MM-DD')
              : null,
            note: contractRecord.note,
          },

          // Page 2
          [RENEW_CONTRACT_MODAL_WIZARD_PAGE.PAGE_2_COVERED_LOCATIONS]: {
            ...pageData[RENEW_CONTRACT_MODAL_WIZARD_PAGE.PAGE_2_COVERED_LOCATIONS],
            cover_spaces,
          },

        },
      }, async () => {
        // TODO: more initialisation
        // Initialisation complete
        this.setState({
          initialising: false,
        });
      });
    });
  }


  /**
   * Performs all of the actions which actually represent the contract renewal
   *
   * For the moment I can't think of a better way to process this sequential data in the time allotted.... yuk
   */
  private processContractRenewal = async () => {
    // Reset the state of the renewal data
    this.setState({
      processing: true,
      success: false,
    }, async () => {
      // Execute the Renew Contract action
      const renewContractResult = await this.executeRenewContractAction();
      const { contractRecord } = this.state;

      // Record the result
      this.setState({
        renewContractResult,
        contractRecord: renewContractResult.success ? renewContractResult.result : contractRecord,
      }, async () => {
        // If the contract renewal was successful, the modal can be closed.
        if (renewContractResult.success) {
          // Processing Complete
          this.setState({
            processing: false,
            success: true,
            isDirty: false,
          });
        } else {
          // Update Contract failed
          this.setState({ processing: false, success: false });
        }
      });
    });
  }


  /**
   * Fired by the wizard when the user attempts to progress forward.
   *
   * Returns true if the page validated successfully otherwise it returns an array of data errors
   */
  private handleValidatePageData = (pageIndex: number, pageData: IModalWizardPageData): true | IModalWizardPageDataError[] => {
    const dataErrors: IModalWizardPageDataError[] = [];

    switch (pageIndex as A_RENEW_CONTRACT_MODAL_WIZARD_PAGE) {
      // Page 1 - Activating Purchase Order Information
      case RENEW_CONTRACT_MODAL_WIZARD_PAGE.PAGE_1_NEW_CONTRACT_SETTINGS: {
        const typedPageData = pageData as IRenewContractModalWizardPage1Data;
        if (!typedPageData.start_date) {
          dataErrors.push({ fieldName: 'start_date', error: 'The order start date is required.' });
        }
        break;
      }
    }

    return (dataErrors.length > 0) ? dataErrors : true;
  };


  /**
   * Fired by the wizard when the user changes any of the page data and just before the state is updated.
   * This gives us a chance to hook into any of the data changes and make additional changes prior to being committed to the state.
   *
   * Return PageData with alterations to augment / change / hook the change of page data
   * Return null to leave the pageData unchanged.
   */
  private handleBeforePageDataChanged = (pageIndex: number, pageData: IModalWizardPageData): null | IModalWizardPageData => {
    switch (pageIndex as A_RENEW_CONTRACT_MODAL_WIZARD_PAGE) {
      // Page 1
      case RENEW_CONTRACT_MODAL_WIZARD_PAGE.PAGE_1_NEW_CONTRACT_SETTINGS: {
        const { pageData: oldPageData } = this.state;
        const { initialData: contractRecord } = this.props;
        const typedPageData = pageData as IRenewContractModalWizardPage1Data;
        const oldPage1Data = oldPageData[RENEW_CONTRACT_MODAL_WIZARD_PAGE.PAGE_1_NEW_CONTRACT_SETTINGS];

        // The renewal is an automatically_generate_renewal and the user changed automatically_generate_renewal or the start date
        // Update the auto-generated order number
        if (
          (typedPageData.automatically_generate_renewal && (
            (typedPageData.automatically_generate_renewal !== oldPage1Data.automatically_generate_renewal) ||
            (typedPageData.start_date !== oldPage1Data.start_date)
          ))
        ) {
          typedPageData.start_date = contractRecord.end_date;
          typedPageData.end_date = contractRecord.contract_months ?
            moment(contractRecord.end_date).add(contractRecord.contract_months, 'months').format('Y-MM-DD')
            : null;
        }

        return typedPageData;
      }

      // Page 2
      case RENEW_CONTRACT_MODAL_WIZARD_PAGE.PAGE_2_COVERED_LOCATIONS: {
        const typedPageData = pageData as IRenewContractModalWizardPage2Data;
        return {
          ...typedPageData,
        };
      }

      // Page 2
      case RENEW_CONTRACT_MODAL_WIZARD_PAGE.PAGE_3_FINALISE: {
        const typedPageData = pageData as IRenewContractModalWizardPage3Data;
        return {
          ...typedPageData,
        };
      }
    }

    return null;
  };


  /**
   * Ensure the right buttons are available at the right time
   */
  private handleCheckButtonVisible = (buttonType: A_MODAL_WIZARD_BUTTON_TYPE, pageIndex: number, defaultButtonVisible: boolean) => {
    const { processing, success } = this.state;

    if (buttonType === MODAL_WIZARD_BUTTON_TYPE.CANCEL) {
      // Don't show the cancel button on the last page unless processing failed
      return (
        (pageIndex < RENEW_CONTRACT_MODAL_WIZARD_PAGE.PAGE_2_COVERED_LOCATIONS)
        ||
        (
          (pageIndex === RENEW_CONTRACT_MODAL_WIZARD_PAGE.PAGE_2_COVERED_LOCATIONS) &&
          (processing === false) &&
          (success === false)
        )
      );
    }

    if (buttonType === MODAL_WIZARD_BUTTON_TYPE.SUBMIT) {
      // Only show the submit button on the last page prior to processing
      return pageIndex === RENEW_CONTRACT_MODAL_WIZARD_PAGE.PAGE_2_COVERED_LOCATIONS;
    }

    if (buttonType === MODAL_WIZARD_BUTTON_TYPE.NEXT) {
      // Don't show the next button on the second or last page
      return pageIndex < RENEW_CONTRACT_MODAL_WIZARD_PAGE.PAGE_2_COVERED_LOCATIONS;
    }

    if (buttonType === MODAL_WIZARD_BUTTON_TYPE.PREV) {
      // Don't show the previous button on the last page unless processing failed
      return (
        (
          (pageIndex > RENEW_CONTRACT_MODAL_WIZARD_PAGE.PAGE_1_NEW_CONTRACT_SETTINGS) &&
          (pageIndex < RENEW_CONTRACT_MODAL_WIZARD_PAGE.PAGE_3_FINALISE)
        )
        ||
        (
          (pageIndex === RENEW_CONTRACT_MODAL_WIZARD_PAGE.PAGE_3_FINALISE) &&
          (processing === false) &&
          (success === false)
        )
      );
    }

    // use the default visibility for all other situations / button types
    return defaultButtonVisible;
  };


  /**
   * Ensure buttons are enabled at the appropriate time
   */
  private handleCheckButtonEnabled = (buttonType: A_MODAL_WIZARD_BUTTON_TYPE, pageIndex: number, defaultButtonEnabled: boolean) => {
    const { initialising, pageData } = this.state;

    // Disable all but the cancel button while initialising
    if (initialising && (buttonType !== MODAL_WIZARD_BUTTON_TYPE.CANCEL)) return false;

    // Disable the "next" button on Page 1 if the contract start date exist
    if (
      (pageIndex === RENEW_CONTRACT_MODAL_WIZARD_PAGE.PAGE_1_NEW_CONTRACT_SETTINGS) &&
      (buttonType === MODAL_WIZARD_BUTTON_TYPE.NEXT) &&
      (!pageData[RENEW_CONTRACT_MODAL_WIZARD_PAGE.PAGE_1_NEW_CONTRACT_SETTINGS].start_date)
    ) {
      return false;
    }

    return defaultButtonEnabled;
  }


  /**
   * Handle the click of one of the wizard buttons
   */
  private handleButtonClick = (buttonType: A_MODAL_WIZARD_BUTTON_TYPE, pageIndex: number): boolean => {
    // If the submit button was clicked, begin processing the renewal
    if ((buttonType === MODAL_WIZARD_BUTTON_TYPE.SUBMIT) && (pageIndex === RENEW_CONTRACT_MODAL_WIZARD_PAGE.PAGE_2_COVERED_LOCATIONS)) {
      // move to the last page
      this.setState({
        currentPageIndex: RENEW_CONTRACT_MODAL_WIZARD_PAGE.PAGE_3_FINALISE,
      },
      // kick off the processing
      this.processContractRenewal);

      // Let the wizard know that the button click was handled
      return true;
    }

    // Returning false indicates that the button click was not handled.
    return false;
  };


  /**
   * Finds a pre-existing contract company of type "Contract Company Space" that is already assigned to the contract
   */
  private loadContractCompanySpaces = async (): Promise<SERObject<IContractCompanySpaceRecord[]>> => {
    const result = new SERObject<IContractCompanySpaceRecord[]>(false);

    const {
      apiProvider: { apiFetch },
      initialData: {
        id: contract_id,
      },
    } = this.props;

    if (this.loadContractCompanySpacesAbortController) this.loadContractCompanySpacesAbortController.abort();

    this.loadContractCompanySpacesAbortController = apiAborter();

    const response = await apiFetch([
      `/contract/${contract_id}/space?with[]=companySpace`,
      'with[]=subtype:id,name',
      'pagelength=5000',
    ].join('&'),
    {
      name: 'ActivateContractModalWizard::loadContractCompanySpaces',
      signal: this.loadContractCompanySpacesAbortController.signal,
    });

    if (response.success) {
      this.loadContractCompanySpacesAbortController = null;
      if (response.body.data) {
        result.result = response.body.data;
        result.success = true;
      }
    } else if (!response.aborted) {
      this.loadContractCompanySpacesAbortController = null;
      console.error('Failed to execute a search for a Contract Contract CompanySpace Company: ', response.error, response.body);
      result.error = (response.body && response.body.errors) ? response.body.errors : response.error;
    }

    return result;
  }


  /**
   * Call the Renew Contract action
   */
  private executeRenewContractAction = async () => {
    const renewContractResult = new SERObject<IContractRecord>(false);

    const {
      pageData,
    } = this.state;

    const {
      apiProvider: { apiFetch },
      initialData: { id: contract_id },
    } = this.props;

    let abortController = this.renewContractAbortController;
    if (abortController) {
      abortController.abort();
    }
    abortController = apiAborter();
    this.renewContractAbortController = abortController;

    // Get data from Page 1
    const {
      clone_notes,
      clone_serials,
      automatically_generate_renewal,
      contract_months,
      start_date,
      end_date,
      note,
    } = pageData[RENEW_CONTRACT_MODAL_WIZARD_PAGE.PAGE_1_NEW_CONTRACT_SETTINGS];

    // Get data from Page 2
    const {
      cover_spaces,
    } = pageData[RENEW_CONTRACT_MODAL_WIZARD_PAGE.PAGE_2_COVERED_LOCATIONS];

    // Get data from Page 3
    // const {} = pageData[RENEW_CONTRACT_MODAL_WIZARD_PAGE.PAGE_3_FINALISE];

    const response = await apiFetch(
      `/contract/${contract_id}/action/renew`,
      {
        name: 'RenewContractModalWizard::executeRenewContractAction',
        method: HTTP_METHOD.POST,
        signal: abortController.signal,
        body: {
          clone_notes,
          clone_serials,
          automatically_generate_renewal,
          note,
          contract_months,
          start_date,
          end_date,
          cover_spaces,
        },
      },
    );

    if (response.success) {
      renewContractResult.result = response.body.data;
      renewContractResult.success = true;
    } else {
      console.error('Failed to create Contract Order: ', response.error, response.body);
      renewContractResult.error = (response.body && response.body.errors) ? response.body.errors : response.error;
    }

    return renewContractResult;
  }

  /**
   * @inheritdoc
   */
  public render = (): JSX.Element => {
    const {
      initialData: {
        number,
      },
      id: modal_id,
      closeModal,
      onModalComplete,
    } = this.props;

    const {
      isDirty,
      currentPageIndex,
    } = this.state;

    return (
      <ModalWizard<IRenewContractModalWizardState, ConfirmActionModalResult>
        {...this.props}
        color={THEME_COLOR.PRIMARY}
        size="lg"
        title={`Renew Contract ${number}`}
        allowDismiss={!isDirty && currentPageIndex === 0}
        isDirty={isDirty || currentPageIndex > 0}
        pageComponents={[
          RenewContractModalPage1,
          RenewContractModalPage2,
          RenewContractModalPage3,
        ]}
        wizardState={this.state}
        setWizardState={(newState: unknown, callback?: () => void) => {
          this.setState(newState as IRenewContractModalWizardState, callback);
        }}
        onValidatePageData={this.handleValidatePageData}
        onPageIndexChanged={() => this.setState({ isDirty: true })}
        onPageDataChanged={() => this.setState({ isDirty: true })}
        onBeforePageDataChanged={this.handleBeforePageDataChanged}
        onModalCancel={() => closeModal(modal_id, isDirty)}
        onModalComplete={() => onModalComplete(true)}
        onCheckButtonVisible={this.handleCheckButtonVisible}
        onCheckButtonEnabled={this.handleCheckButtonEnabled}
        onButtonClick={this.handleButtonClick}
      />
    );
  }
}


// Wrap the Renew Contract Modal Wizard in an API Provider
export const RenewContractModalWizard = connectToAPIProvider(RenewContractModalWizardComponent);
